import React, { useState, useEffect } from 'react';
import {
  UserGroupIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
  ClockIcon,
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { autoplannerAPI } from '../../../services/api';

const ConfirmationSummary = ({ formData, trainingGroups, taskId }) => {
  const [normalizedData, setNormalizedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch normalized dataset
  useEffect(() => {
    const fetchNormalizedData = async () => {
      if (!taskId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await autoplannerAPI.getNormalizedDataset(taskId);
        if (response.success && response.normalized_data?.success) {
          console.log('Normalized data received:', response.normalized_data.normalized_data);
          setNormalizedData(response.normalized_data.normalized_data);
        } else {
          console.error(
            'Failed to load normalized data:',
            response.error || response.normalized_data?.error
          );
          setError(
            response.error || response.normalized_data?.error || 'Failed to load normalized data'
          );
        }
      } catch (err) {
        console.error('Error fetching normalized data:', err);
        setError('Failed to load configuration data');
      } finally {
        setLoading(false);
      }
    };

    fetchNormalizedData();
  }, [taskId]);

  // Use only normalized data - KISS principle
  const selectedTrainingGroup = normalizedData?.display_data?.training_group;
  const selectedSessions = (normalizedData?.display_data?.session_details || []).sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateA.getTime() - dateB.getTime();
  });
  const lockedSeatings = normalizedData?.locked_seatings || {};
  const sessionExclusions = normalizedData?.display_data?.session_exclusions || {};
  const resolutions = normalizedData?.display_data?.resolutions || {};
  const userDetails = normalizedData?.display_data?.user_details || {};
  const crewDetails = normalizedData?.display_data?.crew_details || [];
  const sessionOverrides = normalizedData?.display_data?.session_overrides || {};

  const getPromptTypeLabel = (type) => {
    const typeLabels = {
      crew_consistent: 'Consistent crew members',
      crew_mixing: 'Mixing crew members',
      availability_preference: 'Preferred availability',
      consistent_times: 'Consistent training times',
      coach_consistent: 'Consistent crew and coach matching',
      coach_mixing: 'Mixing crew and coach matching',
    };
    return typeLabels[type] || type;
  };

  const formatDate = (date) => {
    return format(new Date(date), 'MMM dd, yyyy');
  };

  const getUserName = (userId) => {
    const user = userDetails[String(userId)];
    return user?.name || `User ${userId}`;
  };

  const getCrewName = (crewId) => {
    const crew = crewDetails.find((c) => c.id === parseInt(crewId));
    return crew?.name || `Crew ${crewId}`;
  };

  const getSessionTitle = (sessionId) => {
    const session = selectedSessions.find((s) => s.id === parseInt(sessionId));
    return session?.title || `Session ${sessionId}`;
  };

  const getAvailabilityStatus = (resolutions, userId, date) => {
    const userResolutions = resolutions[userId] || {};
    const resolution = userResolutions[date];

    if (resolution === 'assume_available') {
      return { status: 'assumed', label: 'Assumed Available', color: 'text-green-600' };
    } else if (resolution === 'ignore') {
      return { status: 'ignored', label: 'Ignored', color: 'text-red-600' };
    }
    return { status: 'normal', label: 'Normal', color: 'text-gray-600' };
  };

  const getLockedSeatingsSummary = () => {
    const summary = {
      crews: [],
    };

    Object.entries(lockedSeatings).forEach(([crewId, crewData]) => {
      const setups = crewData.setups || [];
      const crewLockedDetails = [];

      setups.forEach((setup) => {
        const lockedPositions = [];
        Object.entries(setup.seating || {}).forEach(([seatNumber, userId]) => {
          if (userId !== null) {
            const userName = getUserName(userId);
            lockedPositions.push({
              seat: seatNumber,
              userId,
              userName,
            });
          }
        });

        if (lockedPositions.length > 0) {
          crewLockedDetails.push({
            setupName: setup.name || 'Setup',
            sessions: setup.sessions || [],
            lockedPositions,
          });
        }
      });

      if (crewLockedDetails.length > 0) {
        summary.crews.push({
          crewId,
          crewName: getCrewName(crewId),
          lockedDetails: crewLockedDetails,
        });
      }
    });

    return summary;
  };

  const lockedSeatingsSummary = getLockedSeatingsSummary();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">
              {taskId ? 'Loading configuration details...' : 'Preparing configuration summary...'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <span className="ml-2 text-sm text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!normalizedData) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-6">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            <span className="ml-2 text-sm text-yellow-700">
              No configuration data available. Please ensure the task has been properly set up.
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Basic Task Information */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-medium text-gray-900">Task Overview</h3>
          <p className="mt-1 text-sm text-gray-600">
            Basic configuration for the autoplanner task. This information defines the scope and
            parameters for algorithm powered crew scheduling.
          </p>
        </div>
        <div className="space-y-6 p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <UserGroupIcon className="h-6 w-6 text-gray-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">Training Group</h4>
              <p className="text-sm text-gray-600">{selectedTrainingGroup?.name || 'Unknown'}</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <CalendarDaysIcon className="h-6 w-6 text-gray-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">Training Sessions</h4>
              <p className="text-sm text-gray-600">{selectedSessions.length} session(s) selected</p>
              {formData.startDate && formData.endDate && (
                <p className="mt-1 text-sm text-gray-500">
                  Date range: {format(new Date(formData.startDate), 'MMM dd')} -{' '}
                  {format(new Date(formData.endDate), 'MMM dd, yyyy')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-gray-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">Algorithm Preferences</h4>
              <p className="text-sm text-gray-600">
                Type: {getPromptTypeLabel(formData.promptType)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sessions Detail */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-medium text-gray-900">Selected Sessions</h3>
          <p className="mt-1 text-sm text-gray-600">
            Training sessions that will be scheduled. The algorithm will create optimal crew
            assignments for each session based on availability and preferences.
          </p>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {selectedSessions.map((session) => (
              <div
                key={session.id}
                className="flex items-center justify-between rounded-lg bg-gray-50 p-3"
              >
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">{session.title}</h4>
                  <p className="text-sm text-gray-600">
                    {formatDate(session.date)} • {session.duration} minutes
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-gray-500">Selected</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Locked Seatings Summary */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center space-x-2">
            <LockClosedIcon
              className={`h-5 w-5 ${lockedSeatingsSummary.crews.length > 0 ? 'text-blue-500' : 'text-gray-400'}`}
            />
            <h3 className="text-lg font-medium text-gray-900">Locked Seating Configuration</h3>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Pre-assigned rower positions that the algorithm must respect. These locked seatings will
            be maintained across all applicable sessions.
          </p>
        </div>
        <div className="p-6">
          {lockedSeatingsSummary.crews.length > 0 ? (
            <div className="space-y-4">
              {lockedSeatingsSummary.crews.map((crew) => (
                <div key={crew.crewId} className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">{crew.crewName}</h4>
                    <LockClosedIcon className="h-4 w-4 text-blue-500" />
                  </div>

                  {crew.lockedDetails.map((setup, setupIndex) => (
                    <div key={setupIndex} className="mb-3 last:mb-0">
                      <h5 className="mb-2 text-xs font-medium text-blue-800">{setup.setupName}</h5>

                      {/* Sessions this setup applies to */}
                      <div className="mb-2">
                        <span className="text-xs font-medium text-blue-700">
                          Applies to sessions:
                        </span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {setup.sessions.map((sessionId, sessionIndex) => {
                            const session = selectedSessions.find((s) => s.id === sessionId);
                            return (
                              <span
                                key={sessionIndex}
                                className="inline-flex items-center rounded bg-blue-200 px-2 py-1 text-xs text-blue-800"
                              >
                                {session ? session.title : `Session ${sessionId}`}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      {/* Locked positions */}
                      <div className="space-y-1">
                        <span className="text-xs font-medium text-blue-700">Locked positions:</span>
                        {setup.lockedPositions.map((position, posIndex) => (
                          <div
                            key={posIndex}
                            className="flex items-center justify-between rounded bg-blue-100 px-2 py-1 text-xs"
                          >
                            <span className="text-blue-800">
                              Seat {position.seat}: {position.userName}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              No locked seating configurations have been set up.
            </p>
          )}
        </div>
      </div>

      {/* Session Exclusions */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center space-x-2">
            <ExclamationTriangleIcon
              className={`h-5 w-5 ${Object.keys(sessionExclusions).length > 0 ? 'text-orange-500' : 'text-gray-400'}`}
            />
            <h3 className="text-lg font-medium text-gray-900">Session Exclusions</h3>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Sessions that specific crews will not participate in. The algorithm will exclude these
            crews from scheduling for the specified sessions. These sessions have been excluded,
            usually due to missing availability data.
          </p>
        </div>
        <div className="p-6">
          {Object.keys(sessionExclusions).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(sessionExclusions).map(([crewId, excludedSessions]) => (
                <div key={crewId} className="rounded-lg bg-orange-50 p-3">
                  <h4 className="text-sm font-medium text-gray-900">{getCrewName(crewId)}</h4>
                  <p className="mb-2 text-sm text-gray-600">
                    {excludedSessions.length} session(s) excluded
                  </p>
                  <div className="space-y-1">
                    {excludedSessions.map((sessionId) => {
                      const session = selectedSessions.find((s) => s.id === parseInt(sessionId));
                      return (
                        <div
                          key={sessionId}
                          className="rounded bg-orange-100 px-2 py-1 text-xs text-gray-600"
                        >
                          • {getSessionTitle(sessionId)} (
                          {formatDate(session?.date || 'Unknown date')})
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600">No session exclusions have been configured.</p>
          )}
        </div>
      </div>

      {/* Session Availability Overrides */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center space-x-2">
            <ClockIcon
              className={`h-5 w-5 ${Object.keys(sessionOverrides).length > 0 ? 'text-blue-500' : 'text-gray-400'}`}
            />
            <h3 className="text-lg font-medium text-gray-900">Session Availability Overrides</h3>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Per-session availability adjustments applied in validation. These affect scheduling only
            for the specified session(s).
          </p>
        </div>
        <div className="p-6">
          {Object.keys(sessionOverrides).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(sessionOverrides)
                .sort(([, a], [, b]) => {
                  const dateA = a?.date ? new Date(a.date).getTime() : 0;
                  const dateB = b?.date ? new Date(b.date).getTime() : 0;
                  return dateA - dateB;
                })
                .map(([sessionId, ov]) => {
                  const title = getSessionTitle(sessionId);
                  const date = ov?.date ? formatDate(ov.date) : 'Unknown date';
                  const crews = Array.isArray(ov?.crews) ? ov.crews : [];

                  // Collect all members from all crews
                  const allMembers = crews.flatMap((crew) =>
                    Array.isArray(crew.members) ? crew.members : []
                  );

                  return (
                    <div key={sessionId} className="rounded-lg bg-blue-50 p-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">{title}</h4>
                        <span className="text-xs text-blue-700">{date}</span>
                      </div>
                      {ov?.duration_minutes ? (
                        <div className="mt-1 text-xs text-blue-700">
                          Window duration: {ov.duration_minutes} minutes
                        </div>
                      ) : null}
                      {allMembers.length > 0 ? (
                        <div className="mt-2 space-y-1">
                          {allMembers.map((m, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between rounded bg-blue-100 px-2 py-1 text-xs"
                            >
                              <span className="text-blue-800">{getUserName(m.user_id)}</span>
                              <span className="text-blue-700">
                                {(m.range?.start || '') && (m.range?.end || '')
                                  ? `${m.range.start}-${m.range.end}`
                                  : 'available in window'}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-2 text-xs text-gray-600">
                          No member-specific overrides listed.
                        </p>
                      )}
                    </div>
                  );
                })}
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              No session availability overrides have been configured.
            </p>
          )}
        </div>
      </div>

      {/* Availability Resolutions */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center space-x-2">
            <ClockIcon
              className={`h-5 w-5 ${Object.keys(resolutions).length > 0 ? 'text-green-500' : 'text-gray-400'}`}
            />
            <h3 className="text-lg font-medium text-gray-900">Availability Resolutions</h3>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Manual decisions for missing availability data. "Assume Available" treats the rower as
            fully available, "Ignore" excludes them from scheduling.
          </p>
        </div>
        <div className="p-6">
          {Object.keys(resolutions).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(resolutions).map(([userId, userResolutions]) => (
                <div key={userId} className="rounded-lg bg-green-50 p-3">
                  <h4 className="text-sm font-medium text-gray-900">
                    {getUserName(parseInt(userId))}
                  </h4>
                  <div className="mt-2 space-y-1">
                    {Object.entries(userResolutions)
                      .sort(([dateA], [dateB]) => {
                        const timeA = new Date(dateA).getTime();
                        const timeB = new Date(dateB).getTime();
                        return timeA - timeB;
                      })
                      .map(([date, resolution]) => {
                        const status = getAvailabilityStatus(resolutions, userId, date);
                        return (
                          <div key={date} className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">{formatDate(date)}</span>
                            <span className={status.color}>{status.label}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              No availability resolutions have been configured.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmationSummary;
